import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  approveNutrition,
  getAdminNutrition,
  getReviewHistory,
  previewScore,
  rejectNutrition,
  saveNutrition,
} from "../../network/service/NutritionService";
import { canReviewNutrition } from "../../network/service/PlatformAuth";
import CustomerPreview from "../products/nutrition/customer-preview";
import GradeCard from "../products/nutrition/grade-card";
import NutritionFields from "../products/nutrition/nutrition-fields";
import {
  fromProfile,
  macrosValid,
  SOURCE_NAMES,
  toPreviewBody,
  toSaveBody,
} from "../products/nutrition/nutrition-model";
import "../products/editor/product-editor.css";
import { formatDate, REVIEW_LABELS } from "./nutrition-review";
import ReviewAccess from "./review-access";
import { gradeName } from "../products/nutrition/grade-names";

/**
 * One product's nutrition for an expert: edit the label, then approve or
 * reject it. Saving changes sends the product back to "waiting for review";
 * approving with unsaved changes saves them first.
 */
function NutritionReviewDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const canDecide = canReviewNutrition();

  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(null);
  const [report, setReport] = useState(null);
  const [sideView, setSideView] = useState("customer");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const edited = useRef(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [nutrition, reviews] = await Promise.all([getAdminNutrition(productId), getReviewHistory(productId)]);
      setData(nutrition);
      setHistory(reviews);
      if (nutrition.profile) setForm(fromProfile(nutrition.profile, nutrition.ingredientFacts));
      setReport(nutrition.health);
      edited.current = false;
      setDirty(false);
    } catch (e) {
      setError(e.message);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live grade while editing.
  useEffect(() => {
    if (!form || !edited.current || !macrosValid(form)) return;
    const timer = setTimeout(async () => {
      try {
        setReport(await previewScore(toPreviewBody(form)));
      } catch {
        // The last grade stays visible; saving reports real errors.
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form]);

  const setNutrition = (updates) => {
    edited.current = true;
    setDirty(true);
    setMessage("");
    setForm((f) => ({ ...f, ...updates }));
  };

  const save = async () => {
    if (!macrosValid(form)) throw new Error("Energy, protein, carbohydrates and fat are required.");
    // Changed values are not verified until an expert approves them.
    await saveNutrition(productId, toSaveBody({ ...form, isVerified: false }));
  };

  const run = async (label, action, done) => {
    setError("");
    setMessage("");
    setBusy(label);
    try {
      await action();
      await load();
      setMessage(done);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const onSave = () => run("save", save, "Changes saved. The product is waiting for review again.");

  const onApprove = () =>
    run(
      "approve",
      async () => {
        if (dirty) await save();
        await approveNutrition(productId, note.trim());
        setNote("");
      },
      "Approved. Customers now see these values as verified.",
    );

  const onReject = () => {
    if (!note.trim()) {
      setError("Write why the nutrition is rejected, so the catalog team can fix it.");
      return;
    }
    run(
      "reject",
      async () => {
        if (dirty) await save();
        await rejectNutrition(productId, note.trim());
        setNote("");
        setRejecting(false);
      },
      "Rejected.",
    );
  };

  if (!data) {
    return (
      <main className="container-fluid dashboard">
        {error ? (
          <div className="alert alert-danger">
            {error} <button className="nutri-link" onClick={load}>Retry</button>
          </div>
        ) : (
          <p className="nutri-muted">Loading…</p>
        )}
      </main>
    );
  }

  const profile = data.profile;
  // Left out of the grade while empty (it is then "Good" at most).
  const missingForGrade = form
    ? [["sugar", "sugars"], ["saturatedFat", "saturated fat"], ["sodium", "sodium"]]
        .filter(([k]) => form[k] === "")
        .map(([, label]) => label)
    : [];
  const name = data.product?.name ?? `Product #${productId}`;
  const state = profile?.reviewStatus ?? "PENDING";

  return (
    <main className="container-fluid dashboard pe-page">
      <div className="pe-header">
        <button type="button" className="btn btn-sm btn-white-bg" onClick={() => navigate("/nutrition-review")}>
          ← Back
        </button>
        <h3 className="dashboard-title mb-0">{name}</h3>
        {report && (
          <span className="pe-grade-chip" style={{ background: report.color }}>
            {report.eatProteinScore !== null ? `${gradeName(report.grade)} · ${report.eatProteinScore}` : "Not yet rated"}
          </span>
        )}
        <span className={`review-badge review-${state.toLowerCase()}`}>{REVIEW_LABELS[state]}</span>
      </div>

      {!profile ? (
        <div className="pe-layout">
          <div className="nutri-status nutri-status-warn">
            This product has no nutrition data yet, so there is nothing to review.
          </div>
        </div>
      ) : (
        <div className="pe-layout">
          <div className="pe-main">
            <section className="pe-card">
              <h5>Review</h5>
              <p className="nutri-muted mb-2">
                Source: <strong>{SOURCE_NAMES[profile.source] ?? profile.source}</strong>
                {profile.matchConfidence !== null && profile.matchConfidence < 1 && (
                  <> · filled by name match ({Math.round(profile.matchConfidence * 100)}%)</>
                )}
              </p>
              {state !== "PENDING" && (
                <div className={`nutri-status ${state === "APPROVED" ? "nutri-status-ok" : "nutri-status-error"}`}>
                  {REVIEW_LABELS[state]} by {profile.reviewedByName ?? "an expert"} on {formatDate(profile.reviewedAt)}
                  {profile.reviewNote && <div>“{profile.reviewNote}”</div>}
                </div>
              )}
              {missingForGrade.length > 0 && (
                <div className="nutri-status nutri-status-warn">
                  {missingForGrade.join(", ")} {missingForGrade.length > 1 ? "are" : "is"} missing and left out of the
                  grade, so it is "Good" at most. Enter the label values below for a complete grade.
                </div>
              )}
              {dirty && (
                <div className="nutri-status nutri-status-info">
                  You changed the values. {canDecide ? "Approving or rejecting saves them first." : ""} Saving
                  sends the product back to “waiting for review”.
                </div>
              )}

              {!canDecide && (
                <div className="nutri-status nutri-status-info">
                  View only: a nutrition expert approves or rejects this product.
                </div>
              )}

              {canDecide && (
                <label className="nutri-field">
                  <span>{rejecting ? "Why is it rejected? *" : "Note (optional)"}</span>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={note}
                    placeholder={rejecting ? "e.g. Protein on the pack is 8 g, not 18 g" : ""}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
              )}

              {message && <p className="text-success mb-0 mt-2">{message}</p>}
              {error && <p className="text-danger mb-0 mt-2">{error}</p>}

              <div className="review-actions">
                {dirty && (
                  <button type="button" className="btn btn-white-bg" onClick={onSave} disabled={!!busy}>
                    {busy === "save" ? "Saving…" : "Save changes"}
                  </button>
                )}
                {canDecide && !rejecting && (
                  <>
                    <button type="button" className="btn btn-danger" onClick={() => setRejecting(true)} disabled={!!busy}>
                      Reject
                    </button>
                    <button type="button" className="btn btn-success" onClick={onApprove} disabled={!!busy}>
                      {busy === "approve" ? "Approving…" : dirty ? "Save & approve" : "Approve"}
                    </button>
                  </>
                )}
                {canDecide && rejecting && (
                  <>
                    <button type="button" className="btn btn-white-bg" onClick={() => setRejecting(false)} disabled={!!busy}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-danger" onClick={onReject} disabled={!!busy}>
                      {busy === "reject" ? "Rejecting…" : "Confirm reject"}
                    </button>
                  </>
                )}
              </div>
            </section>

            <section className="pe-card">
              <h5>Nutrition label</h5>
              <NutritionFields
                form={form}
                set={setNutrition}
                detectedAllergens={data.ingredientFacts?.detectedAllergens}
                hideVerified
              />
            </section>

            <section className="pe-card">
              <h5>Review history</h5>
              {history.length === 0 && <p className="nutri-muted mb-0">Not reviewed yet.</p>}
              <ul className="review-history">
                {history.map((r) => (
                  <li key={r.id}>
                    <span className={`review-badge review-${r.status.toLowerCase()}`}>{REVIEW_LABELS[r.status]}</span>{" "}
                    by <strong>{r.reviewedByName ?? "an expert"}</strong> · {formatDate(r.createdAt)} ·{" "}
                    {SOURCE_NAMES[r.source] ?? r.source}
                    {r.grade && ` · grade ${r.grade === "UNRATED" ? "not rated" : gradeName(r.grade)}`}
                    {r.note && <div className="nutri-muted">“{r.note}”</div>}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="pe-side">
            <div className="nutri-tabs">
              {[["customer", "Customer view"], ["details", "Score details"]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`nutri-tab${sideView === value ? " active" : ""}`}
                  onClick={() => setSideView(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {report && sideView === "customer" && (
              <CustomerPreview report={report} name={name} brand={form.brandName} form={form} />
            )}
            {report && sideView === "details" && <GradeCard report={report} />}
          </aside>
        </div>
      )}
    </main>
  );
}

export default function NutritionReviewDetailPage() {
  return (
    <ReviewAccess>
      <NutritionReviewDetail />
    </ReviewAccess>
  );
}
